// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { JobType, ShiftNameType } from "../../PlacementCandidate/types";
import { getPlacementCardType } from "../utils";
import { INCENTIVE_PROGRAM, type IncentiveProgram } from "./useGetIncentives";
import { type PlacementDetail } from "./useGetPlacement";

export const PLACEMENT_TYPE = {
  unverified: "unverified",
  verified: "verified",
} as const;
export const PLACEMENT_TYPES = Object.values(PLACEMENT_TYPE);
export type PlacementType = (typeof PLACEMENT_TYPES)[number];

export const MANAGEMENT_TYPE = {
  assisted: "assisted",
  independent: "independent",
} as const;
export const MANAGEMENT_TYPES = Object.values(MANAGEMENT_TYPE);
export type ManagementType = (typeof MANAGEMENT_TYPES)[number];

export const workerApplicationPreferenceDetailsSchema = z.object({
  jobTypesInterested: z.array(z.nativeEnum(JobType)),
  shiftTypesInterested: z.array(z.nativeEnum(ShiftNameType)),
  yearsOfExperienceByQualification: z.record(z.string(), z.number()),
  notes: z.string(),
  preferredDaysOfWeek: z.array(z.nativeEnum(DayOfTheWeek)),
});

export type WorkerApplicationPreferenceDetails = z.infer<
  typeof workerApplicationPreferenceDetailsSchema
>;

export const createPlacementApplicationBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.placementApplication),
    attributes: z
      .object({
        scheduleInterview: z
          .object({
            start: z.string(),
            end: z.string(),
          })
          .optional(),
        incentiveProgram: z.nativeEnum(INCENTIVE_PROGRAM).optional(),
        workerVoiceNote: z
          .object({
            rootFolder: z.string(),
            fileKey: z.string(),
          })
          .optional(),
        workerInterviewRequest: z
          .object({
            dates: z.array(z.coerce.date()).min(1),
            notes: z.string(),
          })
          .optional(),
        placementType: z.nativeEnum(PLACEMENT_TYPE).optional(),
        workerApplicationPreferenceDetails: workerApplicationPreferenceDetailsSchema.optional(),
        applicationManagementType: z.nativeEnum(MANAGEMENT_TYPE).optional(),
      })
      .optional(),
    relationships: z.object({
      placementCandidate: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placementCandidate),
          id: z.string(),
        }),
      }),
      placement: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placement),
          id: z.string(),
        }),
      }),
      hiringEvent: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.hiringEvent),
            id: z.string(),
          }),
        })
        .optional(),
    }),
  }),
});
export type CreatePlacementApplicationBody = z.infer<typeof createPlacementApplicationBodySchema>;

export const placementApplicationResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(API_TYPES.placementApplication),
    attributes: z.object({
      status: z.string(),
      appliedDate: z.string(),
      workerVoiceNote: z
        .object({
          rootFolder: z.string(),
          fileKey: z.string(),
        })
        .optional(),
      workerInterviewRequest: z
        .object({
          dates: z.array(z.string()),
          notes: z.string(),
        })
        .optional(),
    }),
    relationships: z.object({
      placement: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placement),
          id: z.string(),
        }),
      }),
      placementCandidate: z.object({
        data: z.object({
          type: z.literal(API_TYPES.placementCandidate),
          id: z.string(),
        }),
      }),
    }),
  }),
  included: z.array(
    z.discriminatedUnion("type", [
      z.object({
        type: z.literal(API_TYPES.placement),
        id: z.string(),
        attributes: z.object({
          name: z.string(),
        }),
        relationships: z.object({
          workplace: z.object({
            data: z.object({
              type: z.literal(API_TYPES.workplace),
              id: z.string(),
            }),
          }),
        }),
      }),
      z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
        attributes: z.object({
          name: z.string(),
        }),
      }),
      z.object({
        type: z.literal(API_TYPES.placementCandidate),
        id: z.string(),
        attributes: z.object({
          status: z.string(),
          accessId: z.string(),
        }),
        relationships: z.object({
          worker: z.object({
            data: z.object({
              type: z.literal(API_TYPES.worker),
              id: z.string(),
            }),
          }),
        }),
      }),
      z.object({
        type: z.literal(API_TYPES.worker),
        id: z.string(),
        attributes: z.object({
          name: z.string(),
          email: z.string(),
          phone: z.string(),
        }),
      }),
      z.object({
        type: z.literal(API_TYPES.interview),
        id: z.string(),
        attributes: z.object({
          start: z.string(),
          end: z.string(),
          status: z.string(),
        }),
      }),
    ])
  ),
});

export type PlacementApplicationResponse = z.infer<typeof placementApplicationResponseSchema>;

export interface CreatePlacementApplicationParams {
  placementCandidateId: string;
  placementDetails: PlacementDetail;
  scheduleInterview?: {
    start: string;
    end: string;
  };
  hiringEventId?: string;
  incentiveProgram?: IncentiveProgram;
  workerVoiceNote?: {
    rootFolder: string;
    fileKey: string;
  };
  workerInterviewRequest?: {
    dates: Date[];
    notes: string;
  };
  placementType?: PlacementType;
  workerApplicationPreferenceDetails?: WorkerApplicationPreferenceDetails;
  applicationManagementType?: "assisted" | "independent" | undefined;
}

export function useCreatePlacementApplication(
  options: UseMutationOptions<
    PlacementApplicationResponse,
    string,
    CreatePlacementApplicationParams
  > = {}
) {
  const history = useHistory();
  const cardType = getPlacementCardType(new URLSearchParams(history.location.search));

  return useMutation<PlacementApplicationResponse, string, CreatePlacementApplicationParams>(
    ["createPlacementApplication"],
    async (params) => {
      const {
        placementCandidateId,
        placementDetails,
        scheduleInterview,
        hiringEventId,
        incentiveProgram,
        workerVoiceNote,
        workerInterviewRequest,
        placementType,
        workerApplicationPreferenceDetails,
        applicationManagementType,
      } = params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/placement-applications`,
        data: {
          data: {
            type: API_TYPES.placementApplication,
            attributes: {
              ...(isDefined(scheduleInterview) && {
                scheduleInterview,
              }),
              ...(incentiveProgram ? { incentiveProgram } : undefined),
              ...(isDefined(workerVoiceNote) && {
                workerVoiceNote,
              }),
              ...(isDefined(workerInterviewRequest) && {
                workerInterviewRequest,
              }),
              ...(isDefined(placementType) && {
                placementType,
              }),
              ...(isDefined(workerApplicationPreferenceDetails) && {
                workerApplicationPreferenceDetails,
              }),
              ...(isDefined(applicationManagementType) ? { applicationManagementType } : undefined),
            },
            relationships: {
              placementCandidate: {
                data: {
                  type: API_TYPES.placementCandidate,
                  id: placementCandidateId,
                },
              },
              placement: {
                data: {
                  type: API_TYPES.placement,
                  id: placementDetails.id,
                },
              },
              ...(isDefined(hiringEventId) &&
                (cardType === "open-house" || !isDefined(cardType)) && {
                  hiringEvent: {
                    data: {
                      type: API_TYPES.hiringEvent,
                      id: hiringEventId,
                    },
                  },
                }),
            },
          },
        },
        responseSchema: placementApplicationResponseSchema,
        requestSchema: createPlacementApplicationBodySchema,
      });

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_APPLICATION_CREATED, {
        placementCandidateId,
        placementDetails,
        type: isDefined(scheduleInterview) ? "BOOKED_INTERVIEW" : "APPLIED",
        scheduleInterview,
      });

      return response.data;
    },
    {
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.PLACEMENT_APPLICATION_CREATE_FAILURE,
      },
      ...options,
    }
  );
}
